/**
 * Component: sa-product-hotspot
 * Description: Interactive image hotspot Web Component.
 *   Desktop (hover device):
 *     - mouseenter dot  → show cursor-following tooltip with product title + price
 *     - mousemove canvas → move tooltip to cursor position
 *     - mouseleave dot  → hide tooltip
 *     - click dot       → navigate to product page
 *   Mobile (touch device):
 *     - first tap dot   → show popup card above the dot
 *     - tap card        → navigate to product page
 *     - tap outside     → close all popups
 */
if (!customElements.get('sa-product-hotspot')) {
  class SaProductHotspot extends HTMLElement {
    constructor() {
      super();
      this.handleDotEnter = this.handleDotEnter.bind(this);
      this.handleDotLeave = this.handleDotLeave.bind(this);
      this.handleDotClick = this.handleDotClick.bind(this);
      this.handleCanvasMouseMove = this.handleCanvasMouseMove.bind(this);
      this.handleOutsideClick = this.handleOutsideClick.bind(this);
    }

    connectedCallback() {
      this.dots = Array.from(this.querySelectorAll('.sa-product-hotspot__dot'));
      this.canvas = this.querySelector('.sa-product-hotspot__canvas');
      this.isHoverDevice = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      if (this.isHoverDevice) {
        this.tooltip = this._buildTooltip();
        document.body.appendChild(this.tooltip);

        this.dots.forEach((dot) => {
          dot.addEventListener('mouseenter', this.handleDotEnter);
          dot.addEventListener('mouseleave', this.handleDotLeave);
        });

        if (this.canvas) {
          this.canvas.addEventListener('mousemove', this.handleCanvasMouseMove);
        }
      }

      this.dots.forEach((dot) => {
        dot.addEventListener('click', this.handleDotClick);
      });

      document.addEventListener('click', this.handleOutsideClick);
    }

    disconnectedCallback() {
      this.dots.forEach((dot) => {
        dot.removeEventListener('mouseenter', this.handleDotEnter);
        dot.removeEventListener('mouseleave', this.handleDotLeave);
        dot.removeEventListener('click', this.handleDotClick);
      });

      if (this.canvas) {
        this.canvas.removeEventListener('mousemove', this.handleCanvasMouseMove);
      }

      if (this.tooltip && this.tooltip.parentNode) {
        this.tooltip.parentNode.removeChild(this.tooltip);
      }

      document.removeEventListener('click', this.handleOutsideClick);
    }

    // ── Desktop handlers ──────────────────────────────────────────

    handleDotEnter(e) {
      const dot = e.currentTarget;
      const title = dot.dataset.productTitle || '';
      const price = dot.dataset.productPrice || '';
      const compare = dot.dataset.productCompare || '';
      const image = dot.dataset.productImage || '';

      this.titleEl.textContent = title;
      this.priceEl.innerHTML = '';

      if (compare) {
        const sale = document.createElement('span');
        sale.className = 'sa-product-hotspot__tooltip-price--sale';
        sale.textContent = price;

        const was = document.createElement('span');
        was.className = 'sa-product-hotspot__tooltip-price--compare';
        was.textContent = compare;

        this.priceEl.appendChild(sale);
        this.priceEl.appendChild(was);
      } else {
        const regular = document.createElement('span');
        regular.className = 'sa-product-hotspot__tooltip-price--regular';
        regular.textContent = price;
        this.priceEl.appendChild(regular);
      }

      if (image) {
        this.imgEl.src = image;
        this.imgWrap.hidden = false;
      } else {
        this.imgWrap.hidden = true;
      }

      // Cache canvas bounds for overflow detection on each enter
      this.canvasRect = this.canvas ? this.canvas.getBoundingClientRect() : null;

      this.tooltip.classList.add('is-visible');
    }

    handleDotLeave() {
      this.tooltip.classList.remove('is-visible');
    }

    handleCanvasMouseMove(e) {
      if (!this.tooltip || !this.canvasRect) return;

      const gap = 16;
      const tw = this.tooltip.offsetWidth;
      const th = this.tooltip.offsetHeight;
      const cr = this.canvasRect;

      let x = e.clientX + gap;
      let y = e.clientY + gap;

      // Overflow right edge → flip left
      if (x + tw > cr.right) {
        x = e.clientX - tw - gap;
      }

      // Overflow bottom edge → flip up
      if (y + th > cr.bottom) {
        y = e.clientY - th - gap;
      }

      // Hard clamp to canvas left / top after flipping
      if (x < cr.left) x = cr.left;
      if (y < cr.top) y = cr.top;

      this.tooltip.style.transform = `translate(${x}px, ${y}px)`;
    }

    // ── Shared click handler ──────────────────────────────────────

    handleDotClick(e) {
      const dot = e.currentTarget;
      const productUrl = dot.dataset.productUrl;

      if (e.target.closest('.sa-product-hotspot__card')) {
        return;
      }

      e.preventDefault();

      if (this.isHoverDevice) {
        if (productUrl) window.location.href = productUrl;
      } else {
        if (dot.classList.contains('is-active')) {
          if (productUrl) window.location.href = productUrl;
        } else {
          e.stopPropagation();
          this._closeAll();
          this._openPopup(dot);
        }
      }
    }

    handleOutsideClick(e) {
      if (!e.target.closest('.sa-product-hotspot__dot')) {
        this._closeAll();
      }
    }

    // ── Helpers ───────────────────────────────────────────────────

    _openPopup(dot) {
      const popup = dot.querySelector('.sa-product-hotspot__popup');
      dot.classList.add('is-active');
      if (popup) popup.removeAttribute('aria-hidden');
    }

    _closePopup(dot) {
      const popup = dot.querySelector('.sa-product-hotspot__popup');
      dot.classList.remove('is-active');
      if (popup) popup.setAttribute('aria-hidden', 'true');
    }

    _closeAll() {
      this.dots.forEach((dot) => this._closePopup(dot));
    }

    _buildTooltip() {
      const el = document.createElement('div');
      el.className = 'sa-product-hotspot__tooltip';
      el.setAttribute('aria-hidden', 'true');

      // Image
      this.imgWrap = document.createElement('div');
      this.imgWrap.className = 'sa-product-hotspot__tooltip-image';

      this.imgEl = document.createElement('img');
      this.imgEl.className = 'sa-product-hotspot__tooltip-img';
      this.imgEl.alt = '';
      this.imgWrap.appendChild(this.imgEl);

      // Body
      const body = document.createElement('div');
      body.className = 'sa-product-hotspot__tooltip-body';

      this.titleEl = document.createElement('p');
      this.titleEl.className = 'sa-product-hotspot__tooltip-title';

      this.priceEl = document.createElement('div');
      this.priceEl.className = 'sa-product-hotspot__tooltip-price';

      body.appendChild(this.titleEl);
      body.appendChild(this.priceEl);

      // Arrow
      const arrow = document.createElement('span');
      arrow.className = 'sa-product-hotspot__tooltip-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '›';

      el.appendChild(this.imgWrap);
      el.appendChild(body);
      el.appendChild(arrow);
      return el;
    }
  }

  customElements.define('sa-product-hotspot', SaProductHotspot);
}
